import { createModel } from "@/orm/model";
import { BelongsToIds, Entity, EntityRecord, belongsToIds } from "@/orm/types";
import { Route } from "@/paths";
import { CurrencyCode } from "@/utils/moneyFormating";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { matchPath } from "react-router-dom";
import { DealRecord } from "./deal";

export type FinancialDeckRecord = EntityRecord & {
  name: string;
  loanType: "sba7a" | "conventional";
  currencyCode: CurrencyCode;
  allowedEmails?: string[];
  copyedFromUserId: string | null;
  copyedFromEmail: string | null;
  excerpt?: {
    targetPurchasePrice: number | null;
    cashFlow: number | null;
  };
  belongsToDealIds: BelongsToIds<DealRecord>;
};

export const financialDeckModel = createModel<FinancialDeckRecord>({
  version: "1.0",
  collectionPath: "modelEntityRecordFinancialDeck",
  labels: {
    singular: "Financial Model",
    plural: "Financial Models",
  },
  getDefaultRecord: () => ({
    name: "",
    loanType: "sba7a",
    currencyCode: "USD",
    copyedFromUserId: null,
    copyedFromEmail: null,
    allowedEmails: [],
    excerpt: {
      targetPurchasePrice: 0,
      cashFlow: 0,
    },
    belongsToDealIds: belongsToIds([]),
  }),
});

export const cloneFinancialDeck = async (
  deckEntity: Entity<FinancialDeckRecord>,
  financialDeckValue: Partial<FinancialDeckRecord> = {},
  revisionValue: Record<string, unknown> = {},
) => {
  if (!deckEntity.data) throw new Error("Deck not hydrated");

  const newDeck = await financialDeckModel.create({
    ...deckEntity.data,
    name: `${deckEntity.data.name} (copy)`,
    belongsToDealIds: belongsToIds<DealRecord>([]),
    allowedEmails: [],
    ...financialDeckValue,
  });

  const revisionRef = doc(deckEntity.docRef, "revision", "latest");
  const latestRevisionSnap = await getDoc(revisionRef);
  const latestRevisionSnapData = {
    ...latestRevisionSnap.data(),
    ...revisionValue,
  };

  if (latestRevisionSnapData) {
    const newRevisionRef = doc(newDeck.docRef, "revision", "latest");
    await setDoc(newRevisionRef, latestRevisionSnapData);
  }

  return newDeck;
};

export const getPathFinancialDeckId = () => {
  return matchPath({ path: Route.Model, end: false }, location.pathname)?.params
    .modelId;
};

export const getCurrentFinancialDeckEntity = () => {
  const id = getPathFinancialDeckId();

  if (!id) {
    throw new Error("No deal ID found in the path");
  }

  const record = financialDeckModel.createEntityInstance({ id });
  return record;
};
